import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const PluginApplicationDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PluginApplication"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"getMemberApplication"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"Boolean"}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"pluginId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"pluginApplication"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"pluginId"}}},{"kind":"Argument","name":{"kind":"Name","value":"forMember"},"value":{"kind":"Variable","name":{"kind":"Name","value":"getMemberApplication"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"allowedOrigins"}},{"kind":"Field","name":{"kind":"Name","value":"key"}},{"kind":"Field","name":{"kind":"Name","value":"secret"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PluginApplication","document":PluginApplicationDocument}} as const;
export type PluginApplicationQueryVariables = Types.Exact<{
  getMemberApplication?: Types.InputMaybe<Types.Scalars['Boolean']['input']>;
  pluginId: Types.Scalars['String']['input'];
}>;


export type PluginApplicationQuery = (
  { __typename: 'Query' }
  & { pluginApplication?: Types.Maybe<(
    { __typename: 'Application' }
    & Pick<Types.Application, 'id' | 'allowedOrigins' | 'key' | 'secret'>
  )> }
);

/**
 * __usePluginApplicationQuery__
 *
 * To run a query within a React component, call `usePluginApplicationQuery` and pass it any options that fit your needs.
 * When your component renders, `usePluginApplicationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePluginApplicationQuery({
 *   variables: {
 *      getMemberApplication: // value for 'getMemberApplication'
 *      pluginId: // value for 'pluginId'
 *   },
 * });
 */
export function usePluginApplicationQuery(
  baseOptions: TrelloQueryHookOptions<
    PluginApplicationQuery,
    PluginApplicationQueryVariables
  > &
    (
      | { variables: PluginApplicationQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PluginApplicationDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PluginApplicationQuery,
    PluginApplicationQueryVariables
  >(PluginApplicationDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePluginApplicationLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PluginApplicationQuery,
    PluginApplicationQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    PluginApplicationQuery,
    PluginApplicationQueryVariables
  >(PluginApplicationDocument, options);
}
export function usePluginApplicationSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PluginApplicationQuery,
        PluginApplicationQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    PluginApplicationQuery,
    PluginApplicationQueryVariables
  >(PluginApplicationDocument, options);
}
export type PluginApplicationQueryHookResult = ReturnType<
  typeof usePluginApplicationQuery
>;
export type PluginApplicationLazyQueryHookResult = ReturnType<
  typeof usePluginApplicationLazyQuery
>;
export type PluginApplicationSuspenseQueryHookResult = ReturnType<
  typeof usePluginApplicationSuspenseQuery
>;
export type PluginApplicationQueryResult = Apollo.QueryResult<
  PluginApplicationQuery,
  PluginApplicationQueryVariables
>;
